<template>
    <div :style="{'max-width': '99%', 'width': '98%', 'margin-left': 'auto', 'margin-right': 'auto', 'margin-top': '10px'}">

        <!-- Detailed View Modal -->
        <b-modal id="modal-detailed-view" scrollable
            :title="`DETAILED RECEIVING HISTORY - ${selectedProduct ? selectedProduct.product_name : ''} (${selectedProduct ? selectedProduct.sku : ''})`"
            size="xl"
            hide-footer
            style="min-height: 700px;">

            <div v-if="selectedProduct" class="detailed-modal">
                <!-- Product Summary -->
                <b-card class="mb-3">
                    <b-row>
                        <b-col>
                            <strong>Category:</strong> {{ packet.categoriesdict[selectedProduct.category_id] }}
                        </b-col>
                        <b-col>
                            <strong>Total Locations:</strong> {{ getUniqueLocations }}
                        </b-col>
                        <b-col>
                            <strong>Total Received By:</strong> {{ getUniqueReceivers }}
                        </b-col>
                        <b-col>
                            <strong>Total Units Received:</strong> {{ getDetailedTotalReceived }}
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col>
                            <strong>Domestic Orders:</strong> {{ getDetailedDomesticOrders }}
                        </b-col>
                        <b-col>
                            <strong>Transfer Orders:</strong> {{ getDetailedTransferOrders }}
                        </b-col>
                        <b-col>
                            <strong>Customer Backorders:</strong> {{ getDetailedCustomerBackorders }}
                        </b-col>
                    </b-row>
                </b-card>

                <!-- Modal Filters -->
                <b-card class="mb-3">
                    <b-row class="filters-row">
                        <b-col md="4">
                            <label><strong>DATE RANGE</strong></label>
                            <b-form-select :options="dateRangeOptions" v-model="modalDateRange" @change="fetchDetailedData" class="mb-2">
                            </b-form-select>
                        </b-col>
                        <b-col md="4">
                            <label><strong>LOCATION</strong></label>
                            <b-form-select :options="packet.locations" v-model="modalLocationId" @change="fetchDetailedData" class="mb-2">
                            </b-form-select>
                        </b-col>
                    </b-row>
                </b-card>

                <!-- Location-wise Data Table -->
                <div v-if="locationWiseData.length > 0">
                    <h6 class="mb-3">Receiving by Location</h6>
                    <div style="overflow-x: auto;">
                        <b-table striped hover
                                :items="locationWiseData"
                                :fields="locationFields"
                                small
                                style="min-width: 700px;"
                                class="detailed-table">
                            <template #cell(location_name)="data">
                                <b-button
                                    variant="link"
                                    class="p-0 location-link"
                                    @click="showReceiverModal(data.item)">
                                    <strong>{{ data.value || 'N/A' }}</strong>
                                </b-button>
                            </template>
                            <template #cell(total_received)="data">
                                <strong>{{ data.value || 0 }}</strong>
                            </template>
                            <template #cell(actions)="data">
                                <b-button
                                    variant="outline-primary"
                                    size="sm"
                                    @click="showReceiverModal(data.item)">
                                    <b-icon icon="people-fill"></b-icon> View
                                </b-button>
                            </template>
                        </b-table>
                    </div>
                </div>

                <div v-else class="text-center py-4">
                    <b-alert variant="warning" show>
                        <b-icon icon="exclamation-triangle"></b-icon>
                        No detailed receiving data available for the selected filters
                    </b-alert>
                </div>
            </div>
        </b-modal>

        <!-- Receiver Modal -->
        <b-modal v-model="receiverModalVisible"
                :title="`Receivers - ${selectedLocation ? selectedLocation.location_name : 'Location'}`"
                size="lg"
                hide-footer
                centered
                class="receiver-modal">

            <div v-if="selectedLocation" class="receiver-content">
                <!-- Location Summary -->
                <b-card class="mb-3">
                    <b-row>
                        <b-col>
                            <strong>Location:</strong> {{ selectedLocation.location_name }}
                        </b-col>
                        <b-col>
                            <strong>Total Received:</strong> {{ selectedLocation.total_received || 0 }}
                        </b-col>
                        <b-col>
                            <strong>Receivers:</strong> {{ receiverData.length }}
                        </b-col>
                    </b-row>
                </b-card>

                <!-- Receiver Table -->
                <div class="table-scroll-container receiver-table-container" v-if="receiverData.length > 0">
                    <b-table striped hover
                            :items="receiverData"
                            :fields="receiverFields"
                            small
                            class="receiver-table">
                        <template #cell(received_by)="data">
                            <b-badge variant="outline-primary">{{ data.value || 'N/A' }}</b-badge>
                        </template>
                        <template #cell(total_received)="data">
                            <strong>{{ data.value || 0 }}</strong>
                        </template>
                    </b-table>
                </div>

                <div v-else class="text-center py-4">
                    <b-alert variant="info" show>
                        <b-icon icon="info-circle"></b-icon>
                        No receiver data available for this location
                    </b-alert>
                </div>

                <!-- Modal Actions -->
                <div class="modal-actions mt-3">
                    <b-button variant="outline-secondary" @click="receiverModalVisible = false">
                        Close
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- Receiving Details Modal -->
        <b-modal id="modal-receiving-details" scrollable
            :title="`RECEIVING TRANSACTIONS - ${selectedProduct ? selectedProduct.product_name : ''} (${selectedProduct ? selectedProduct.sku : ''})`"
            size="xl"
            hide-footer
            style="min-height: 700px;">

            <div v-if="selectedProduct" class="receiving-details-modal">
                <!-- Summary Card -->
                <b-card class="mb-3 bg-light">
                    <b-row>
                        <b-col>
                            <strong>Total Transactions:</strong> {{ filteredReceivingDetails.length }}
                        </b-col>
                        <b-col>
                            <strong>Total Quantity Received:</strong> {{ getTotalQuantityReceived }}
                        </b-col>
                        <b-col>
                            <strong>Average per Transaction:</strong> {{ getAveragePerTransaction.toFixed(1) }}
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col>
                            <strong>Domestic Orders:</strong> {{ getTotalDomesticOrders }}
                        </b-col>
                        <b-col>
                            <strong>Transfer Orders:</strong> {{ getTotalTransferOrders }}
                        </b-col>
                        <b-col>
                            <strong>Customer Backorders:</strong> {{ getTotalCustomerBackorders }}
                        </b-col>
                    </b-row>
                </b-card>

                <!-- Receiving Details Filters -->
                <b-card class="mb-3">
                    <b-row class="filters-row align-items-end">
                        <b-col md="auto" class="flex-grow-1">
                            <label class="small mb-1"><strong>DATE RANGE</strong></label><br>
                            <date-picker
                                v-model="receivingDateRange"
                                type="date"
                                range
                                placeholder="Select date range"
                                value-type="YYYY-MM-DD"
                                format="MMM DD, YYYY"
                                @input="applyReceivingDetailsFilters"
                                class="mb-2"
                                style="width:200px !important;">
                            </date-picker>
                        </b-col>

                        <b-col md="auto">
                            <label class="small mb-1"><strong>LOCATION</strong></label><br>
                            <b-form-select
                                :options="locationOptions"
                                v-model="receivingLocationFilter"
                                @change="applyReceivingDetailsFilters"
                                class="mb-2"
                                style="width: 200px;">
                            </b-form-select>
                        </b-col>
                        <b-col md="auto">
                            <label class="small mb-1"><strong>ORDER TYPE</strong></label><br>
                            <b-form-select
                                :options="sourceTypeOptions"
                                v-model="sourceTypeFilter"
                                @change="applyReceivingDetailsFilters"
                                class="mb-2"
                                style="width: 200px;">
                            </b-form-select>
                        </b-col>
                        <b-col md="auto">
                            <label class="small mb-1"><strong>RECEIVED BY</strong></label><br>
                            <b-form-select
                                :options="receiverOptions"
                                v-model="receiverFilter"
                                @change="applyReceivingDetailsFilters"
                                class="mb-2"
                                style="width: 200px;">
                            </b-form-select>
                        </b-col>

                        <b-col md="auto" class="align-self-end">
                            <b-button
                                v-if="hasActiveReceivingDetailsFilters"
                                variant="outline-danger"
                                size="sm"
                                @click="clearReceivingDetailsFilters"
                                class="mb-2">
                                <b-icon icon="x-circle"></b-icon> Clear
                            </b-button>
                        </b-col>
                    </b-row>
                    <b-row v-if="hasActiveReceivingDetailsFilters" class="mt-2">
                        <b-col>
                            <small class="text-muted">
                                Showing {{ filteredReceivingDetails.length }} of {{ receivingDetails.length }} transactions
                            </small>
                        </b-col>
                    </b-row>
                </b-card>

                <!-- Receiving Details Table -->
                <div v-if="filteredReceivingDetails.length > 0" style="overflow-x: auto">
                    <b-table striped hover
                            :items="filteredReceivingDetails"
                            :fields="receivingDetailsFields"
                            small
                            class="receiving-details-table">
                        <template #cell(received_date)="data">
                            {{ formatDate(data.value) }}
                        </template>
                        <template #cell(location_name)="data">
                            {{ data.value || 'N/A' }}
                        </template>
                        <template #cell(received_by)="data">
                            <b-badge variant="info">{{ data.value || 'System' }}</b-badge>
                        </template>
                        <template #cell(transaction_type)="data">
                            <b-badge :variant="getTransactionVariant(data.value)">
                                {{ data.value }}
                            </b-badge>
                        </template>
                        <template #cell(source_type)="data">
                            <small class="text-muted">{{ data.value || 'N/A' }}</small>
                        </template>
                        <template #cell(current_price)="data">
                            ${{ data.value.toFixed(2) }}
                        </template>
                        <template #cell(landed_cost)="data">
                            ${{ data.value.toFixed(2) }}
                        </template>
                        <template #cell(total_cost)="data">
                            <strong>${{ data.value.toFixed(2) }}</strong>
                        </template>
                        <template #cell(quantity_received)="data">
                            <b-badge variant="success">{{ data.value }}</b-badge>
                        </template>
                    </b-table>
                </div>

                <div v-else class="text-center py-4">
                    <b-alert variant="info" show>
                        <b-icon icon="info-circle"></b-icon>
                        No individual receiving transactions found
                        <span v-if="hasActiveReceivingDetailsFilters"> matching your filters</span>
                    </b-alert>
                </div>
            </div>
        </b-modal>

        <!-- Loading State -->
        <div v-if="items_being_loaded == true">
            <loading :active="items_being_loaded"
                :can-cancel="false"
                :is-full-page="true">
            </loading>
        </div>

        <div v-else>
            <!-- Header Controls -->
            <div class="mb-3">
                <!-- Search Filter -->
                <b-alert variant="primary" show style="display: inline-block; margin-left: 10px;">
                    <div>SEARCH BY NAME OR SKU</div>
                    <b-form-input
                        v-model="searchQuery"
                        placeholder="Type product name or SKU..."
                        @input="applyFilters"
                        style="width: 300px; max-width: 300px;"
                        debounce="300">
                    </b-form-input>
                </b-alert>

                <b-alert variant="primary" show style="display: inline-block; margin-left: 10px;">
                    <div>CATEGORY</div>
                    <b-form-select :options="packet.categories" v-model="category_id" @change="applyFilters" style="width: 100%; max-width: 250px;">
                    </b-form-select>
                </b-alert>

                <b-alert variant="primary" show style="display: inline-block; margin-left: 10px;">
                    <div>DATE RANGE</div>
                    <b-form-select :options="dateRangeOptions" v-model="dateRange" @change="applyFilters" style="width: 100%; max-width: 200px;">
                    </b-form-select>
                </b-alert>

                <b-alert variant="primary" show style="display: inline-block; margin-left: 10px;">
                    <div>LOCATION</div>
                    <b-form-select :options="packet.locations" v-model="location_id" @change="applyFilters" style="width: 100%; max-width: 250px;">
                    </b-form-select>
                </b-alert>
            </div>

            <!-- SUMMARY VIEW -->
            <div v-if="items.length > 0" class="diver">
                <table style="width: 100%;">
                    <thead>
                        <tr style="">
                            <th class="srr" style="width: 250px;">ITEM DESCRIPTION</th>
                            <th class="srr" style="width: 150px;">ITEM SKU</th>
                            <th class="srr" style="width: 150px;">CATEGORY</th>
                            <th class="srr" style="width: 80px;">7 DAYS</th>
                            <th class="srr" style="width: 80px;">30 DAYS</th>
                            <th class="srr" style="width: 80px;">60 DAYS</th>
                            <th class="srr" style="width: 80px;">90 DAYS</th>
                            <th class="srr" style="width: 80px;">120 DAYS</th>
                            <th class="srr" style="width: 80px;">ALL TIME</th>
                            <th class="srr" style="width: 120px;">DOMESTIC ORDERS</th>
                            <th class="srr" style="width: 120px;">TRANSFER ORDERS</th>
                            <th class="srr" style="width: 180px;">CUSTOMER BACKORDERS</th>
                            <th class="srr" style="width: 300px;">ACTION</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, idx) in filtered_final" :key="idx">
                            <td class="srrv">{{ item.product_name }}</td>
                            <td class="srrv">{{ item.sku }}</td>
                            <td class="srrv">{{ packet.categoriesdict[item.category_id] }}</td>
                            <td class="srrv">{{ item.received_7d || 0 }}</td>
                            <td class="srrv">{{ item.received_30d || 0 }}</td>
                            <td class="srrv">{{ item.received_60d || 0 }}</td>
                            <td class="srrv">{{ item.received_90d || 0 }}</td>
                            <td class="srrv">{{ item.received_120d || 0 }}</td>
                            <td class="srrv">{{ item.received_so_far || 0 }}</td>
                            <td class="srrv">{{ item.domestic_orders || 0 }}</td>
                            <td class="srrv">{{ item.transfer_orders || 0 }}</td>
                            <td class="srrv">{{ item.customer_backorders || 0 }}</td>
                            <td class="srrv">
                                <b-button size="sm" variant="outline-primary" @click="showDetailedView(item)" class="mr-1">
                                    <b-icon icon="search"></b-icon> DETAILS
                                </b-button>
                                <b-button size="sm" variant="outline-success" @click="showReceivingDetails(item)">
                                    <b-icon icon="box-seam"></b-icon> TRANSACTIONS
                                </b-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="items.length === 0 && !items_being_loaded">
                <b-alert variant="warning" show>
                    <b-icon icon="exclamation-triangle"></b-icon>
                    No receiving history data available
                </b-alert>
            </div>

            <!-- No Results Message -->
            <div v-if="items.length > 0 && filtered_final.length === 0" class="text-center py-4">
                <b-alert variant="info" show>
                    <b-icon icon="search"></b-icon>
                    No products found matching your search criteria.
                    <b-button variant="outline-info" size="sm" @click="clearFilters" class="ml-2">
                        Clear filters
                    </b-button>
                </b-alert>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import Requestor from "@/utilities/Requestor";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/en';

export default {
    name: "ProductsReceivingHistory",
    components: {
        Loading,
        DatePicker
    },
    data: function() {
        return {
            packet: {},
            items: [],
            detailedItems: [],
            receivingDetails: [],
            items_being_loaded: false,
            selectedProduct: null,

            // Main filters
            dateRange: 'all_time',
            category_id: null,
            location_id: null,
            searchQuery: '',
            
            // Modal states
            receiverModalVisible: false,
            selectedLocation: null,
            receiverData: [],

            // Modal filters
            modalDateRange: 'all_time',
            modalLocationId: null,
            
            // Receiving details filters
            receivingDateRange: [],
            receivingLocationFilter: null,
            sourceTypeFilter: null,
            receiverFilter: null,
            
            // Filter options
            locationOptions: [],
            sourceTypeOptions: [],
            receiverOptions: [],

            // Fields for tables
            locationFields: [
                {
                    key: 'location_name',
                    label: 'STORE LOCATION',
                    sortable: true,
                    thStyle: { width: '200px' }
                },
                { key: 'received_7d', label: '7 DAYS', sortable: true },
                { key: 'received_30d', label: '30 DAYS', sortable: true },
                { key: 'received_90d', label: '90 DAYS', sortable: true },
                { key: 'total_received', label: 'TOTAL RECEIVED', sortable: true },
                {
                    key: 'actions',
                    label: 'ACTIONS',
                    thStyle: { width: '150px' }
                }
            ],

            receiverFields: [
                {
                    key: 'received_by',
                    label: 'RECEIVED BY',
                    sortable: true,
                    thStyle: { width: '200px' }
                },
                { key: 'received_7d', label: '7 DAYS', sortable: true },
                { key: 'received_30d', label: '30 DAYS', sortable: true },
                { key: 'received_90d', label: '90 DAYS', sortable: true },
                { key: 'total_received', label: 'TOTAL RECEIVED', sortable: true }
            ],

            receivingDetailsFields: [
                {
                    key: 'received_date',
                    label: 'DATE',
                    sortable: true,
                    thStyle: { width: '120px' }
                },
                {
                    key: 'location_name',
                    label: 'LOCATION',
                    sortable: true,
                    thStyle: { width: '150px' }
                },
                {
                    key: 'received_by',
                    label: 'RECEIVED BY',
                    sortable: true,
                    thStyle: { width: '140px' }
                },
                {
                    key: 'transaction_type',
                    label: 'ORDER TYPE',
                    sortable: true,
                    thStyle: { width: '140px' }
                },
                {
                    key: 'source_type',
                    label: 'SHIP BY',
                    sortable: true,
                    thStyle: { width: '120px' }
                },
                {
                    key: 'from_location',
                    label: 'FROM',
                    sortable: true,
                    thStyle: { width: '130px' }
                },
                {
                    key: 'to_location',
                    label: 'TO',
                    sortable: true,
                    thStyle: { width: '130px' }
                },
                {
                    key: 'quantity_received',
                    label: 'QTY',
                    sortable: true,
                    thStyle: { width: '80px' }
                },
                {
                    key: 'current_price',
                    label: 'PRICE',
                    sortable: true,
                    thStyle: { width: '100px' }
                },
                {
                    key: 'total_cost',
                    label: 'TOTAL COST',
                    sortable: true,
                    thStyle: { width: '120px' }
                }
            ],

            dateRangeOptions: [
                { value: '7_days', text: 'LAST 7 DAYS' },
                { value: '30_days', text: 'LAST 30 DAYS' },
                { value: '60_days', text: 'LAST 60 DAYS' },
                { value: '90_days', text: 'LAST 90 DAYS' },
                { value: '120_days', text: 'LAST 120 DAYS' },
                { value: 'all_time', text: 'ALL TIME' }
            ]
        }
    },
    computed: {
        locationWiseData() {
            const locationMap = new Map();

            this.detailedItems.forEach(item => {
                const locationId = item.location_id;
                const locationName = item.location_name || 'Unknown Location';

                if (!locationMap.has(locationId)) {
                    locationMap.set(locationId, {
                        location_id: locationId,
                        location_name: locationName,
                        received_7d: 0,
                        received_30d: 0,
                        received_90d: 0,
                        total_received: 0
                    });
                }

                const locationData = locationMap.get(locationId);
                locationData.received_7d += item.received_7d || 0;
                locationData.received_30d += item.received_30d || 0;
                locationData.received_90d += item.received_90d || 0;
                locationData.total_received += item.total_received || 0;
            });

            return Array.from(locationMap.values()).sort((a, b) => b.total_received - a.total_received);
        },

        getDetailedTotalReceived() {
            return this.detailedItems.reduce((sum, item) => sum + (item.total_received || 0), 0);
        },

        getDetailedDomesticOrders() {
            return this.detailedItems.reduce((sum, item) => sum + (item.domestic_orders || 0), 0);
        },

        getDetailedTransferOrders() {
            return this.detailedItems.reduce((sum, item) => sum + (item.transfer_orders || 0), 0);
        },

        getDetailedCustomerBackorders() {
            return this.detailedItems.reduce((sum, item) => sum + (item.customer_backorders || 0), 0);
        },

        getUniqueLocations() {
            const locations = new Set(this.detailedItems.map(item => item.location_name).filter(Boolean));
            return locations.size;
        },

        getUniqueReceivers() {
            const receivers = new Set(this.detailedItems.map(item => item.received_by).filter(Boolean));
            return receivers.size;
        },

        filtered_final: function() {
            let items = this.items;

            if (this.category_id) {
                items = items.filter(item => item.category_id == this.category_id);
            }

            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase().trim();
                items = items.filter(item =>
                    item.product_name.toLowerCase().includes(query) ||
                    item.sku.toLowerCase().includes(query)
                );
            }

            return items.slice(0, 300);
        },

        // Receiving details computed properties
        getTotalQuantityReceived: function() {
            return this.filteredReceivingDetails.reduce((sum, item) => sum + (item.quantity_received || 0), 0);
        },

        getTotalDomesticOrders: function() {
            return this.filteredReceivingDetails
                .filter(item => item.transaction_type === 'Domestic Order')
                .reduce((sum, item) => sum + (item.quantity_received || 0), 0);
        },

        getTotalTransferOrders: function() {
            return this.filteredReceivingDetails
                .filter(item => item.transaction_type === 'Transfer Order')
                .reduce((sum, item) => sum + (item.quantity_received || 0), 0);
        },

        getTotalCustomerBackorders: function() {
            return this.filteredReceivingDetails
                .filter(item => item.transaction_type === 'Customer Backorder')
                .reduce((sum, item) => sum + (item.quantity_received || 0), 0);
        },

        getAveragePerTransaction: function() {
            return this.filteredReceivingDetails.length > 0
                ? this.getTotalQuantityReceived / this.filteredReceivingDetails.length
                : 0;
        },

        hasActiveFilters: function() {
            return this.category_id !== null || this.searchQuery !== '';
        },

        filteredReceivingDetails: function() {
            let items = this.receivingDetails;

            // Filter by date range
            if (this.receivingDateRange && this.receivingDateRange.length === 2) {
                const startDate = moment(this.receivingDateRange[0]);
                const endDate = moment(this.receivingDateRange[1]);

                items = items.filter(item => {
                    const receivedDate = moment(item.received_date);
                    return receivedDate.isBetween(startDate, endDate, 'day', '[]');
                });
            }

            // Filter by location
            if (this.receivingLocationFilter) {
                items = items.filter(item =>
                    item.location_id == this.receivingLocationFilter
                );
            }

            // Filter by order type
            if (this.sourceTypeFilter) {
                items = items.filter(item =>
                    item.transaction_type === this.sourceTypeFilter
                );
            }

            // Filter by receiver
            if (this.receiverFilter) {
                items = items.filter(item =>
                    item.received_by === this.receiverFilter
                );
            }

            return items;
        },

        hasActiveReceivingDetailsFilters: function() {
            return (this.receivingDateRange && this.receivingDateRange.length === 2) ||
                   this.receivingLocationFilter !== null ||
                   this.sourceTypeFilter !== null ||
                   this.receiverFilter !== null;
        }
    },
    methods: {
        showReceiverModal(location) {
            if (!location || !location.location_id) {
                console.error('Invalid location data:', location);
                this.$bvToast.toast('Invalid location data', {
                    variant: 'warning',
                    title: 'Error'
                });
                return;
            }

            this.selectedLocation = { ...location };
            this.receiverData = this.getReceiverDataForLocation(location.location_id);
            this.receiverModalVisible = true;
        },

        getReceiverDataForLocation(locationId) {
            if (!this.detailedItems || this.detailedItems.length === 0) {
                return [];
            }

            const receiverMap = new Map();

            this.detailedItems
                .filter(item => item.location_id === locationId)
                .forEach(item => {
                    const receiver = item.received_by || 'Unknown Receiver';

                    if (!receiverMap.has(receiver)) {
                        receiverMap.set(receiver, {
                            received_by: receiver,
                            received_7d: 0,
                            received_30d: 0,
                            received_90d: 0,
                            total_received: 0
                        });
                    }

                    const receiverData = receiverMap.get(receiver);
                    receiverData.received_7d += item.received_7d || 0;
                    receiverData.received_30d += item.received_30d || 0;
                    receiverData.received_90d += item.received_90d || 0;
                    receiverData.total_received += item.total_received || 0;
                });

            return Array.from(receiverMap.values()).sort((a, b) => b.total_received - a.total_received);
        },

        showDetailedView(item) {
            this.selectedProduct = item;
            this.modalDateRange = this.dateRange;
            this.modalLocationId = this.location_id;
            this.fetchDetailedData();
            this.$bvModal.show('modal-detailed-view');
        },

        showReceivingDetails(item) {
            this.selectedProduct = item;
            this.clearReceivingDetailsFilters();
            this.fetchReceivingDetails();
            this.$bvModal.show('modal-receiving-details');
        },

        fetchReceivingDetails() {
            if (!this.selectedProduct) return;

            let that = this;
            let path = "/api/product/get_receiving_history/";
            let data = {
                product_sku: this.selectedProduct.sku,
                view_type: 'receiving_details'
            };

            let s = function(response) {
                if (response.data.result) {
                    that.receivingDetails = response.data.result;
                    that.extractReceivingFilterOptions(response.data.result);
                } else {
                    that.$bvToast.toast(
                        response.data.error || "Error fetching receiving details",
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.receivingDetails = [];
                }
            };

            let f = function(error) {
                console.log('Error fetching receiving details:', error);
                that.$bvToast.toast(
                    "Error fetching receiving details",
                    {title: "Error", autoHideDelay: 1500, variant:"danger"}
                );
                that.receivingDetails = [];
            };

            Requestor.getSyncP(path, data, this, s, f);
        },

        extractReceivingFilterOptions(receivingDetails) {
            // Extract unique locations
            const locationsMap = new Map();
            const sourceTypesMap = new Map();
            const receiversMap = new Map();

            receivingDetails.forEach(item => {
                // Locations
                if (item.location_id && item.location_name) {
                    locationsMap.set(item.location_id, {
                        value: item.location_id,
                        text: item.location_name
                    });
                }

                // Order types (using transaction_type)
                if (item.transaction_type) {
                    sourceTypesMap.set(item.transaction_type, {
                        value: item.transaction_type,
                        text: item.transaction_type
                    });
                }

                // Receivers
                if (item.received_by) {
                    receiversMap.set(item.received_by, {
                        value: item.received_by,
                        text: item.received_by
                    });
                }
            });

            // Convert to arrays with "ALL" option
            this.locationOptions = [
                { value: null, text: "ALL LOCATIONS" },
                ...Array.from(locationsMap.values())
            ];

            this.sourceTypeOptions = [
                { value: null, text: "ALL TYPES" },
                ...Array.from(sourceTypesMap.values())
            ];

            this.receiverOptions = [
                { value: null, text: "ALL RECEIVERS" },
                ...Array.from(receiversMap.values())
            ];
        },

        getTransactionVariant(transactionType) {
            switch(transactionType) {
                case 'Domestic Order': return 'primary';
                case 'Transfer Order': return 'info';
                case 'Customer Backorder': return 'warning';
                case 'New Stock': return 'success';
                default: return 'secondary';
            }
        },

        formatDate(dateString) {
            return moment(dateString).format('MMM DD, YYYY');
        },

        applyReceivingDetailsFilters() {
            // Computed property will automatically update
        },

        clearReceivingDetailsFilters() {
            this.receivingDateRange = [];
            this.receivingLocationFilter = null;
            this.sourceTypeFilter = null;
            this.receiverFilter = null;
        },

        applyFilters() {
            this.fetchSummaryData();
        },

        clearFilters() {
            this.category_id = null;
            this.searchQuery = '';
            this.dateRange = 'all_time';
            this.location_id = null;
            this.fetchSummaryData();
        },

        fetch_init(first) {
            if (first) {
                this.category_id = null;
                this.location_id = null;
                this.dateRange = 'all_time';
                this.searchQuery = '';
            }
            this.fetchSummaryData();
        },

        fetchSummaryData() {
            let that = this;
            let path = "/api/product/get_receiving_history/";
            let data = {
                date_range: this.dateRange,
                category_id: this.category_id,
                location_id: this.location_id,
                view_type: 'summary'
            };

            let s = function(response) {
                if (response.data.result) {
                    that.items = response.data.result.map(item => ({
                        ...item,
                        received_7d: item.received_7d || 0,
                        received_30d: item.received_30d || 0,
                        received_60d: item.received_60d || 0,
                        received_90d: item.received_90d || 0,
                        received_120d: item.received_120d || 0,
                        received_so_far: item.received_so_far || 0,
                        domestic_orders: item.domestic_orders || 0,
                        transfer_orders: item.transfer_orders || 0,
                        customer_backorders: item.customer_backorders || 0
                    }));
                } else {
                    that.$bvToast.toast(
                        response.data.error || "Error fetching receiving history",
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                }
                that.items_being_loaded = false;
            };

            let f = function(error) {
                console.log(error);
                that.$bvToast.toast(
                    "Error fetching receiving history",
                    {title: "Error", autoHideDelay: 1500, variant:"danger"}
                );
                that.items_being_loaded = false;
            };

            this.items_being_loaded = true;
            Requestor.getSyncP(path, data, this, s, f);
        },

        fetchDetailedData() {
            if (!this.selectedProduct) return;

            let that = this;
            let path = "/api/product/get_receiving_history/";
            let data = {
                date_range: this.modalDateRange,
                category_id: this.category_id,
                location_id: this.modalLocationId,
                product_sku: this.selectedProduct.sku,
                view_type: 'detailed'
            };

            let s = function(response) {
                if (response.data.result) {
                    that.detailedItems = response.data.result;
                } else {
                    that.$bvToast.toast(
                        response.data.error || "Error fetching detailed history",
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.detailedItems = [];
                }
            };

            let f = function(error) {
                console.log(error);
                that.$bvToast.toast(
                    "Error fetching detailed history",
                    {title: "Error", autoHideDelay: 1500, variant:"danger"}
                );
                that.detailedItems = [];
            };

            Requestor.getSyncP(path, data, this, s, f);
        }
    },
    created: function() {
        this.packet = JSON.parse(localStorage.packet);
        this.packet.categories.splice(0, 0, {value: null, text: "ALL"});
        this.packet.locations.splice(0, 0, {value: null, text: "ALL"});
        this.packet.userDetails = JSON.parse(localStorage.userDetails);

        // Initialize filter options
        this.locationOptions = [{ value: null, text: "ALL LOCATIONS" }];
        this.sourceTypeOptions = [{ value: null, text: "ALL TYPES" }];
        this.receiverOptions = [{ value: null, text: "ALL RECEIVERS" }];

        this.fetch_init(true);
    }
}
</script>

<style scoped>
.mx-datepicker {
    width: 100%;
}

.mx-input {
    height: 38px;
    font-size: 14px;
    width:100px !important;
}

.b-form-select {
    height: 38px;
    font-size: 14px;
}

@media (max-width: 768px) {
    .filters-row .col-md-3 {
        margin-bottom: 10px;
    }
}

.diver {
    overflow: auto;
    width: 100%;
    height: 600px;
}

td, th {
    border: 1px solid;
}

th {
    background-color: white;
}

table {
    table-layout: fixed;
    width: 100%;
}

.srr {
    font-size: 13px;
    text-align: center;
    background: #a7d4ff;
    border: 1px solid #010101;
    font-weight: bold;
}

.srrv {
    font-size: 12px;
    text-align: center;
    background: white;
    border: 1px solid #010101;
    padding: 5px;
}

.filters-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: end;
}

.detailed-table {
    font-size: 12px;
}

.detailed-table >>> .table th {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
}

.receiving-details-table {
    font-size: 11px;
}

.receiving-details-table >>> .table th {
    background-color: #e9ecef;
    border: 1px solid #dee2e6;
}

.text-right {
    text-align: right;
}

.mr-1 {
    margin-right: 0.25rem;
}

/* Additional styles for receiving */
.receiver-modal .modal-content {
    max-height: 80vh;
}

.receiver-table-container {
    max-height: 400px;
    overflow-y: auto;
}

.receiver-table {
    font-size: 12px;
}

.receiver-table >>> .table th {
    background-color: #f0f8ff;
}

/* Receiving specific badge colors */
.badge-success {
    background-color: #28a745 !important;
}

.badge-primary {
    background-color: #007bff !important;
}

.badge-info {
    background-color: #17a2b8 !important;
}

.badge-warning {
    background-color: #ffc107 !important;
}

.badge-secondary {
    background-color: #6c757d !important;
}

/* Hover effects */
.location-link:hover {
    text-decoration: underline;
    color: #0056b3;
}
</style>


// WEBPACK FOOTER //
// src/components/Receiving/ReceivingHistory.vue